import { inject, Injectable } from '@angular/core';

import { LocalStorageService } from '@efficiently/client-core-browser';
import { ClientLoggerFacadeService } from '@efficiently/client-core-logger';
import { AppConfigService } from '@efficiently/client-core-services';

import { PROJECT_NAVIGATOR } from './constants/project-navigator.constant';

/**
 * What sits between the prefix and the user id in the storage key — `Efficiently-LastProject:<userId>`.
 *
 * Named rather than inlined because the key is assembled in one place and matched by eye in devtools in
 * another; a second spelling of the separator is a key nobody can find and a project nobody resumes at.
 */
const STORAGE_KEY_SEPARATOR = ':';

/**
 * The one event this service emits. Stable by contract: it is what a SIEM query filters on, so it never
 * carries the failure's detail — that is the `failureKind` field below.
 */
const LAST_PROJECT_STORAGE_FAILED_EVENT = 'projects.last_project_storage_failed';

/** Human-readable half of the record. Carries no value, no key and no user — see `reportFailure`. */
const STORAGE_FAILURE_TEXT = 'Last opened project could not be read from or written to local storage';

/**
 * Which half of the contract broke. A field rather than four event names, so one query finds every
 * storage failure and the reason is still there to group by.
 *
 * The values are lower snake_case rather than mirroring their keys, unlike an enum member: they are log
 * field values, and the SIEM's own vocabulary — event names, `failureKind`, `reason` — is snake_case
 * throughout. A `MalformedValue` sitting among them would be the one value a query has to case-match.
 */
const STORAGE_FAILURE_KIND = {
	Read: 'read',
	Write: 'write',
	Clear: 'clear',
	MalformedValue: 'malformed_value',
} as const;

type StorageFailureKind = (typeof STORAGE_FAILURE_KIND)[keyof typeof STORAGE_FAILURE_KIND];

/** Stands in for a thrown value that is not an `Error` at all — `throw 'oops'` is legal JavaScript. */
const UNKNOWN_ERROR_NAME = 'Unknown';

/**
 * The class name of a caught value, and nothing else from it.
 *
 * This is the whole of what {@link LastProjectStorageService.reportFailure} is allowed to say about an
 * error: `QuotaExceededError`, `SecurityError`, `SyntaxError` and `TypeError` are the four a reader is
 * triaging between, and a name cannot carry the message's quoted payload. See that method for why the
 * message is excluded rather than merely trimmed.
 */
function errorClassName(err: unknown): string {
	return err instanceof Error ? err.constructor.name : UNKNOWN_ERROR_NAME;
}

/**
 * Remembers the last project the user opened, so the navigator can resume there.
 *
 * **Why the key carries the user id.** One browser profile is routinely two accounts — a support session
 * beside the customer's own, a shared machine on site — and a shared key would resume the second account
 * at the first account's project, which is a leak of a project id across accounts as much as it is a
 * wrong landing page. With no user id in hand there is therefore no key at all: {@link read} answers
 * `null` and the writes do nothing, rather than falling back to a shared one.
 *
 * **Why every method swallows.** This is a convenience, never a source of truth — the navigator works
 * perfectly with nothing remembered. `LocalStorageService` is thin: `get` runs `JSON.parse` with no
 * `try`, so a value written by anything else on the origin throws a `SyntaxError` on read, and `set`
 * throws once the origin's quota is full or storage is disabled by policy. Letting either escape would
 * take the whole menu down over a preference. The configuration read is guarded separately, in
 * {@link currentUserId}: `config` is declared non-nullable but is only populated at bootstrap, so a call
 * made before it lands is a `TypeError` rather than a missing user id — and that is "no user id", which
 * this service is specified to answer silently, not a storage failure to report.
 *
 * **Why the stored value is validated rather than trusted.** `localStorage` is writable by any script on
 * the origin and survives across sessions, so what comes back is external input: only a non-empty string
 * is handed on as a project id, and a `42`, a `{}` or an empty string reads as nothing stored.
 *
 * `providedIn: 'root'` because the reader and the writer are on opposite sides of the navigator — the
 * menu that resumes and the route that records — and neither is an ancestor of the other.
 */
@Injectable({ providedIn: 'root' })
export class LastProjectStorageService {
	private readonly storage = inject(LocalStorageService);

	private readonly appConfig = inject(AppConfigService);

	/**
	 * `optional`, like `DiagnosticProfileSyncService`'s own injection and for the same reason: the facade
	 * comes from `provideClientLogger()`, not from root, so a consumer that has not called it must still
	 * get a working service rather than a `NullInjectorError` raised from a dependency it never asked
	 * about.
	 */
	private readonly logger = inject(ClientLoggerFacadeService, { optional: true });

	/**
	 * Whether this session has already reported a storage failure.
	 *
	 * The failures this service sees are sticky, not transient: a full quota, storage disabled by policy,
	 * a value some other script wrote. The navigator reads the key on every navigation, so without this
	 * flag one broken profile would ship one `warn` per navigation for the whole session — the same fact
	 * repeated until it drowns the records around it in the SIEM. One report per session is enough to
	 * diagnose a condition that does not change while the tab is open.
	 */
	private hasReportedFailure = false;

	/**
	 * The remembered project id, or `null` when there is nothing to resume at — no user id, nothing
	 * stored, an unreadable value, or storage that refused to answer. Never throws, for any of them.
	 */
	public read(): string | null {
		try {
			const key = this.storageKey();

			if (key === null) {
				return null;
			}

			const stored = this.storage.get<unknown>(key);

			if (stored === null) {
				return null;
			}

			if (typeof stored !== 'string' || stored.trim().length === 0) {
				this.reportFailure(STORAGE_FAILURE_KIND.MalformedValue);

				return null;
			}

			return stored;
		} catch (error) {
			this.reportFailure(STORAGE_FAILURE_KIND.Read, error);

			return null;
		}
	}

	/**
	 * Remembers `projectId` for this user. A blank id stores nothing rather than storing a value
	 * {@link read} would have to reject tomorrow, and with no user id there is no key to write under.
	 */
	public write(projectId: string): void {
		try {
			const key = this.storageKey();

			if (key === null) {
				return;
			}

			const projectIdToStore = projectId.trim();

			if (projectIdToStore.length === 0) {
				return;
			}

			this.storage.set(key, projectIdToStore);
		} catch (error) {
			this.reportFailure(STORAGE_FAILURE_KIND.Write, error);
		}
	}

	/** Forgets this user's remembered project. A no-op with no user id, and never throws. */
	public clear(): void {
		try {
			const key = this.storageKey();

			if (key === null) {
				return;
			}

			this.storage.remove(key);
		} catch (error) {
			this.reportFailure(STORAGE_FAILURE_KIND.Clear, error);
		}
	}

	/**
	 * This user's key, or `null` when the session has no user id — a scope session, or a call made before
	 * the configuration payload landed. `null` means "do nothing", never "use a shared key".
	 */
	private storageKey(): string | null {
		const userId = this.currentUserId();

		if (userId === undefined || userId.length === 0) {
			return null;
		}

		return `${PROJECT_NAVIGATOR.STORAGE_KEY_PREFIX}${STORAGE_KEY_SEPARATOR}${userId}`;
	}

	/**
	 * This session's user id, or `undefined` when there is not one to have.
	 *
	 * `AppConfigService.config` is a getter over a field the bootstrap populates, typed non-nullable
	 * behind a definite-assignment assertion — so a read before it lands is a `TypeError` rather than a
	 * missing id. That window is real and ordinary: a logged-out boot never dispatches the action that
	 * sets it at all, and root services are constructed while the app initializer is still pending.
	 *
	 * It is caught HERE rather than in the callers' `try` so that it reaches the same silent
	 * "no key, do nothing" branch as a scope session. Reporting it would be a `_failed` event asserting a
	 * storage failure that did not happen, and — because the report fires once per session — it would
	 * also spend the one slot a genuine quota or policy failure later needs.
	 */
	private currentUserId(): string | undefined {
		try {
			return this.appConfig.config.user?._id;
		} catch {
			return undefined;
		}
	}

	/**
	 * Reports the first storage failure of the session and nothing after it.
	 *
	 * The record carries the kind and the error's CLASS NAME, and deliberately nothing else: not the key,
	 * not the user id it is built from, not the value that came back, and not the error's message.
	 *
	 * The message is excluded because on both failing paths it quotes exactly what must not ship. A
	 * malformed stored value makes `JSON.parse` throw a `SyntaxError` whose message embeds the value
	 * itself — attacker-writable content, since any script on the origin can write this key — and a
	 * full-quota `setItem` throws a message that embeds the whole storage key, which is the prefix plus
	 * the user id. The client log sanitiser masks by KEY and would pass either through: it reduces an
	 * `Error` to `{ name, message }`, and `err` is not on its denylist. Every signed-in session ships
	 * these records to the SIEM, and the Sentry sink drops only records carrying `errorMessage`.
	 *
	 * The class name answers the question a reader actually has — quota, security policy, parse — and
	 * cannot carry content. Same reasoning, same shape as `FeatureFlagsSyncService`, which excludes an
	 * `ApolloError` message because it can embed query text.
	 */
	private reportFailure(failureKind: StorageFailureKind, err?: unknown): void {
		if (this.hasReportedFailure) {
			return;
		}

		this.hasReportedFailure = true;

		this.logger?.warn(LAST_PROJECT_STORAGE_FAILED_EVENT, STORAGE_FAILURE_TEXT, {
			failureKind,
			...(err === undefined ? {} : { errorName: errorClassName(err) }),
		});
	}
}
